// src/pages/Events/RegistrationForm.js - COMPLETE FILE WITH REFRESH FIX
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  Box,
  TextField,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  Modal,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import SettingsIcon from "@mui/icons-material/Settings";
import { ReactFormBuilder, ReactFormGenerator } from "react-form-builder2";
import "react-form-builder2/dist/app.css"; // Import Form Builder CSS
import { axiosInstance } from "../../utils/axios";
import styled from "@emotion/styled";

// Styled components for form builder layout
const FormBuilderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  flex: 1,
}));

const FormPreviewPanel = styled(Box)(({ theme }) => ({
  width: "35%",
  padding: theme.spacing(2),
  backgroundColor: "#f8f8f8",
  borderRight: "1px solid #ddd",
  overflowY: "auto",
  height: "100%",
}));

const FormBuilderPanel = styled(Box)(({ theme }) => ({
  width: "65%",
  padding: theme.spacing(2),
  backgroundColor: "#fafafa",
  overflowY: "auto",
  height: "100%",
}));

const FormCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[5],
  },
}));

// Modal style
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: 800,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  maxHeight: "80vh",
  overflow: "auto",
  borderRadius: 2,
};

function RegistrationForm({ eventId }) {
  const [openFormBuilder, setOpenFormBuilder] = useState(false);
  const [openOtherForms, setOpenOtherForms] = useState(false);
  const [formData, setFormData] = useState([]);
  const [formName, setFormName] = useState("");
  const [forms, setForms] = useState([]);
  const [defaultForm, setDefaultForm] = useState(null);
  const [otherForms, setOtherForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState("");
  const [eventFormId, setEventFormId] = useState(null); // Form assigned to this event
  const [formDataPreview, setFormDataPreview] = useState([]);
  const [showFormPreview, setShowFormPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedFormSettings, setSelectedFormSettings] = useState({
    isDefault: false,
    description: "",
  });
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [formKey, setFormKey] = useState(0); // Key to force form re-render

  // Helper function to generate a MongoDB ObjectId-like string for preview submissions
  const generatePreviewAttendeeId = () => {
    const timestamp = Math.floor(Date.now() / 1000).toString(16);
    const randomHex = Array.from({ length: 16 }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join("");
    return timestamp + randomHex;
  };

  // Helper function to fetch form data from backend
  const fetchFormData = async (formId) => {
    try {
      console.log("Fetching form data for formId:", formId);

      // First check if we have the form data cached locally
      const cachedForm = forms.find((f) => f._id === formId);
      if (cachedForm && cachedForm.formData && cachedForm.formData.length > 0) {
        console.log("Using cached form data:", cachedForm.formData);
        return cachedForm.formData;
      }

      // Try to fetch from backend - handling potential null formData
      try {
        const response = await axiosInstance.post("/graphql", {
          query: `
            query GetForm($id: ID!) {
              getForm(id: $id) {
                _id
                name
                description
                formData
              }
            }
          `,
          variables: { id: formId },
        });

        if (response.errors) {
          console.warn("GraphQL errors fetching form:", response.errors);
          return [];
        }

        const form = response.data?.getForm;
        if (form && form.formData) {
          console.log(
            "Successfully fetched form data from backend:",
            form.formData
          );
          return Array.isArray(form.formData) ? form.formData : [];
        }

        console.warn("Form found but no formData available");
        return [];
      } catch (backendError) {
        console.warn(
          "Backend fetch failed, using cached data:",
          backendError.message
        );
        return [];
      }
    } catch (error) {
      console.error("Error in fetchFormData:", error);
      return [];
    }
  };

  // Main initialization effect
  useEffect(() => {
    if (eventId && !initialized) {
      initializeData();
    }
  }, [eventId, initialized]);

  // Synchronize forms and defaultFormId
  useEffect(() => {
    const setupDefaultForm = async () => {
      // When both forms and eventFormId are loaded, make sure event form is set
      if (forms.length > 0 && eventFormId) {
        const foundForm = forms.find((form) => form._id === eventFormId);
        if (foundForm) {
          console.log("Setting event form from useEffect:", foundForm.name);
          setDefaultForm(foundForm);
          setSelectedForm(eventFormId);

          // Show form data if available (for newly created forms)
          if (foundForm.formData && foundForm.formData.length > 0) {
            setFormDataPreview(foundForm.formData);
          } else {
            // Fetch form data from backend
            console.log("Fetching event form data from backend");
            const formData = await fetchFormData(eventFormId);
            console.log("Event form data fetched:", formData);
            setFormDataPreview(formData);

            // Don't update forms here to avoid infinite loop
            // The form data is already set in formDataPreview
          }
          setShowFormPreview(true);
        }
      }

      // Update otherForms based on eventFormId
      if (forms.length > 0) {
        if (eventFormId) {
          setOtherForms(forms.filter((form) => form._id !== eventFormId));
        } else {
          setOtherForms(forms);
        }
      }
    };

    setupDefaultForm();
  }, [forms, eventFormId]);

  // Initialize all data in a controlled sequence
  const initializeData = async () => {
    try {
      setLoading(true);

      // First fetch all forms - try to get formData, fallback if null
      const formsResponse = await axiosInstance.post("/graphql", {
        query: `
          query {
            getAllForms {
              _id
              name
              description
              isDefault
              formData
            }
          }
        `,
      });

      if (formsResponse.errors) {
        throw new Error(
          formsResponse.errors[0].message || "Error fetching forms"
        );
      }

      const allForms = (formsResponse.data?.getAllForms || []).map((form) => ({
        ...form,
        formData: Array.isArray(form.formData) ? form.formData : [], // Use actual formData or empty array
      }));
      setForms(allForms);

      // Then fetch the default form
      const defaultResponse = await axiosInstance.post("/graphql", {
        query: `
          query GetEventDefaultForm($eventId: ID!) {
            getEventDefaultForm(eventId: $eventId) {
              _id
              name
              description
              formData
            }
          }
        `,
        variables: {
          eventId,
        },
      });

      if (
        !defaultResponse.errors &&
        defaultResponse.data?.getEventDefaultForm
      ) {
        const defaultFormData = defaultResponse.data.getEventDefaultForm;
        setEventFormId(defaultFormData._id);

        // Find the complete form info from the forms array
        const completeDefaultForm = allForms.find(
          (f) => f._id === defaultFormData._id
        );
        if (completeDefaultForm) {
          setDefaultForm(completeDefaultForm);
          setSelectedForm(completeDefaultForm._id);
          // Show form data if available
          if (
            completeDefaultForm.formData &&
            completeDefaultForm.formData.length > 0
          ) {
            console.log(
              "Using complete default form data:",
              completeDefaultForm.formData
            );
            setFormDataPreview(completeDefaultForm.formData);
          } else {
            // Try to fetch form data for the default form
            console.log("Fetching additional form data for default form");
            const formData = await fetchFormData(completeDefaultForm._id);
            setFormDataPreview(formData);

            // Update the form in our local state to cache the data
            if (formData.length > 0) {
              setForms((prevForms) =>
                prevForms.map((form) =>
                  form._id === completeDefaultForm._id
                    ? { ...form, formData: formData }
                    : form
                )
              );
            }
          }
          setShowFormPreview(true);
        } else {
          // If not found in forms array, use the data from the defaultForm query
          const safeDefaultFormData = {
            ...defaultFormData,
            formData: Array.isArray(defaultFormData.formData)
              ? defaultFormData.formData
              : [],
          };
          setDefaultForm(safeDefaultFormData);
          setSelectedForm(safeDefaultFormData._id);

          // Set form data preview from the default form query result
          if (safeDefaultFormData.formData.length > 0) {
            setFormDataPreview(safeDefaultFormData.formData);
          } else {
            // Try to fetch additional form data
            const formData = await fetchFormData(safeDefaultFormData._id);
            setFormDataPreview(formData);
          }
          setShowFormPreview(true);
        }
      }

      setInitialized(true);
    } catch (error) {
      console.error("Error initializing data:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to load forms",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFormSave = (data) => {
    try {
      // Ensure task_data exists before trying to map it
      if (!data || !data.task_data) {
        console.error("Invalid form data structure:", data);
        return;
      }

      const formattedData = data.task_data.map((item) => ({
        ...item,
        text: item.label || item.text || "", // Ensure 'text' field is present
      }));

      console.log("Form builder data received:", formattedData);
      setFormData(formattedData);
    } catch (error) {
      console.error("Error processing form data:", error);
      setAlert({
        open: true,
        message: "Error processing form data",
        severity: "error",
      });
    }
  };

  const handleFormNameChange = (event) => {
    setFormName(event.target.value);
  };

  const handleSaveForm = async () => {
    if (!formName.trim()) {
      setAlert({
        open: true,
        message: "Please enter a form name",
        severity: "warning",
      });
      return;
    }

    if (!formData.length) {
      setAlert({
        open: true,
        message: "Form cannot be empty. Please add some elements to your form.",
        severity: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation SaveForm($input: FormInput!) {
            saveForm(input: $input) {
              _id
              name
              description
            }
          }
        `,
        variables: {
          input: {
            name: formName,
            data: formData,
            description: "",
            eventId: eventId,
          },
        },
      });

      if (response.errors) {
        throw new Error(response.errors[0].message || "Error saving form");
      }

      if (response.data?.saveForm) {
        console.log("Form saved successfully. Current formData:", formData);
        console.log("FormData length:", formData.length);

        // Keep a copy of the form data before clearing
        const savedFormData = [...formData];

        const newForm = {
          ...response.data.saveForm,
          formData: savedFormData, // Use the actual form data we just created
        };

        console.log("New form object:", newForm);
        console.log("Saved form data:", savedFormData);

        // Add the newly created form to our forms array
        setForms((prevForms) => [...prevForms, newForm]);

        // Set the preview to show the newly created form
        setFormDataPreview(savedFormData);
        setSelectedForm(newForm._id);
        setShowFormPreview(true);

        // Force form key update to ensure re-render
        setFormKey((prev) => prev + 1);

        console.log("Form preview should now show with data:", savedFormData);

        // If no form is assigned to this event yet, assign this one
        if (!eventFormId) {
          await assignFormToEvent(newForm._id);
        }

        setAlert({
          open: true,
          message: "Form saved successfully!",
          severity: "success",
        });

        // Clear form data for next use
        setFormData([]);
        setFormName("");
        setOpenFormBuilder(false);
      } else {
        throw new Error("Failed to save form");
      }
    } catch (error) {
      console.error("Error saving form:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to save form",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSave = async (data) => {
    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation SaveAnswer($input: AnswerInput!) {
            saveAnswer(input: $input) {
              _id
              answers
            }
          }
        `,
        variables: {
          input: {
            formId: selectedForm,
            answers: data,
            attendeeId: generatePreviewAttendeeId(), // Generate unique preview ObjectId
          },
        },
      });

      if (response.errors) {
        throw new Error(response.errors[0].message || "Error saving answers");
      }

      if (response.data?.saveAnswer) {
        setAlert({
          open: true,
          message:
            "Form preview submitted successfully! (This was a test submission)",
          severity: "success",
        });

        // Clear the form by forcing a re-render with a new key
        setFormKey((prev) => prev + 1);
      } else {
        throw new Error("Failed to save answers");
      }
    } catch (error) {
      console.error("Error saving answers:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to save answers",
        severity: "error",
      });
    }
  };

  const handleFormSelect = async (formId) => {
    console.log("Form selected:", formId);
    setSelectedForm(formId);

    // Find the selected form's basic info
    const selected = forms.find((form) => form._id === formId);
    if (selected) {
      console.log("Selected form found:", selected);

      // If this form has formData (newly created forms), use it
      if (selected.formData && selected.formData.length > 0) {
        console.log("Using cached form data:", selected.formData);
        setFormDataPreview(selected.formData);
      } else {
        // Otherwise, fetch the form data from backend
        console.log("Fetching form data from backend for:", formId);
        const formData = await fetchFormData(formId);
        console.log("Fetched form data:", formData);
        setFormDataPreview(formData);

        // Update the form in our local state to cache the data if we got data
        if (formData.length > 0) {
          setForms((prevForms) =>
            prevForms.map((form) =>
              form._id === formId ? { ...form, formData: formData } : form
            )
          );
        }
      }

      // Reset form key to clear any existing form data when switching forms
      setFormKey((prev) => prev + 1);
      setShowFormPreview(true);
      setSelectedFormSettings({
        isDefault: formId === eventFormId,
        description: selected.description || "",
      });
    }

    // Close the modal if it's open
    setOpenOtherForms(false);
  };

  const assignFormToEvent = async (formId) => {
    try {
      setLoading(true);
      console.log("Assigning form to event:", formId);

      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation SetDefaultEventForm($eventId: ID!, $formId: ID!) {
            setDefaultEventForm(eventId: $eventId, formId: $formId) {
              id
              name
              defaultFormId
            }
          }
        `,
        variables: {
          eventId,
          formId,
        },
      });

      if (response.errors) {
        throw new Error(
          response.errors[0].message || "Error assigning form to event"
        );
      }

      setEventFormId(formId);

      // Find the complete form in our forms array
      const formToSet = forms.find((f) => f._id === formId);
      if (formToSet) {
        setDefaultForm(formToSet);
        setSelectedForm(formId);
        // Show form data if available (for newly created forms)
        if (formToSet.formData && formToSet.formData.length > 0) {
          setFormDataPreview(formToSet.formData);
        } else {
          // Fetch form data from backend
          const formData = await fetchFormData(formId);
          setFormDataPreview(formData);

          // Update the form in our local state to cache the data
          setForms((prevForms) =>
            prevForms.map((form) =>
              form._id === formId ? { ...form, formData: formData } : form
            )
          );
        }
        setShowFormPreview(true);
      }

      return true;
    } catch (error) {
      console.error("Error assigning form to event:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to assign form to event",
        severity: "error",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAssignFormToEvent = async (formId) => {
    const success = await assignFormToEvent(formId);

    if (success) {
      // Find the form in our forms array to set as defaultForm
      const formToSet = forms.find((f) => f._id === formId);
      if (formToSet) {
        setDefaultForm(formToSet);
        // Also update selected form to ensure preview works
        setSelectedForm(formId);
        // Show form data if available (for newly created forms)
        if (formToSet.formData && formToSet.formData.length > 0) {
          setFormDataPreview(formToSet.formData);
        } else {
          // Fetch form data from backend
          const formData = await fetchFormData(formId);
          setFormDataPreview(formData);

          // Update the form in our local state to cache the data if we got data
          if (formData.length > 0) {
            setForms((prevForms) =>
              prevForms.map((form) =>
                form._id === formId ? { ...form, formData: formData } : form
              )
            );
          }
        }
        setShowFormPreview(true);
      }

      const formName = formToSet?.name || "Selected form";
      setAlert({
        open: true,
        message: `"${formName}" is now the registration form for this event!`,
        severity: "success",
      });
    }
  };

  const handleOpenFormSettings = (formId) => {
    const form = forms.find((f) => f._id === formId);
    if (form) {
      setSelectedForm(formId);
      setSelectedFormSettings({
        isDefault: form.isDefault || false, // Global default status
        isEventForm: formId === eventFormId, // Event-specific form
        description: form.description || "",
      });
      setSettingsOpen(true);
    }
  };

  const handleSaveFormSettings = async () => {
    try {
      setLoading(true);

      // Find the current form to get its name
      const currentForm = forms.find((f) => f._id === selectedForm);
      if (!currentForm) {
        throw new Error("Form not found");
      }

      // Update form description
      const updateResponse = await axiosInstance.post("/graphql", {
        query: `
          mutation UpdateForm($id: ID!, $input: FormInput!) {
            updateForm(id: $id, input: $input) {
              _id
              name
              description
            }
          }
        `,
        variables: {
          id: selectedForm,
          input: {
            name: currentForm.name,
            description: selectedFormSettings.description,
            data: currentForm.formData || [], // Include the required data field
          },
        },
      });

      if (updateResponse.errors) {
        throw new Error(
          updateResponse.errors[0].message || "Error updating form settings"
        );
      }

      // Update forms array with the new description
      setForms((prevForms) =>
        prevForms.map((form) =>
          form._id === selectedForm
            ? { ...form, description: selectedFormSettings.description }
            : form
        )
      );

      // Update global default form if needed
      if (selectedFormSettings.isDefault) {
        // This would require a mutation to set the form as global default
        // For now, we'll just update the local state
        setForms((prevForms) =>
          prevForms.map((form) => ({
            ...form,
            isDefault: form._id === selectedForm ? true : false,
          }))
        );
      }

      setAlert({
        open: true,
        message: "Form settings updated successfully!",
        severity: "success",
      });

      setSettingsOpen(false);
    } catch (error) {
      console.error("Error updating form settings:", error);
      setAlert({
        open: true,
        message: error.message || "Failed to update form settings",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h3">Registration Form</Typography>
        <Box>
          {forms.length > 0 && (
            <Button
              variant="outlined"
              onClick={() => setOpenOtherForms(true)}
              sx={{ mr: 2 }}
            >
              Choose Different Form
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => setOpenFormBuilder(true)}
            color="primary"
          >
            Create New Form
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {forms.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" gutterBottom>
                No registration forms found
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Create your first form by clicking the "Create New Form" button
              </Typography>
            </Paper>
          ) : (
            <Box>
              {defaultForm ? (
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Registration Form for This Event
                  </Typography>
                  <FormCard
                    sx={{
                      border: "2px solid #1976d2",
                      maxWidth: "100%",
                    }}
                  >
                    <CardContent
                      onClick={() => handleFormSelect(defaultForm._id)}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="h6" gutterBottom>
                          {defaultForm.name}
                        </Typography>
                        <Chip
                          size="small"
                          label="Assigned to Event"
                          color="success"
                        />
                      </Box>
                      {defaultForm.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {defaultForm.description}
                        </Typography>
                      )}
                    </CardContent>
                    <Divider />
                    <CardActions sx={{ justifyContent: "flex-end" }}>
                      <Button
                        startIcon={<SettingsIcon />}
                        size="small"
                        onClick={() => handleOpenFormSettings(defaultForm._id)}
                      >
                        Settings
                      </Button>
                    </CardActions>
                  </FormCard>
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body1" gutterBottom>
                    No default form selected
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Please click "Available Forms" and set one as default
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          {showFormPreview && selectedForm && (
            <Box mt={4}>
              <Typography variant="h5" gutterBottom>
                Form Preview
              </Typography>
              <Paper sx={{ p: 3 }}>
                {(() => {
                  console.log("=== FORM PREVIEW RENDER DEBUG ===");
                  console.log("formDataPreview:", formDataPreview);
                  console.log(
                    "formDataPreview is array:",
                    Array.isArray(formDataPreview)
                  );
                  console.log(
                    "formDataPreview length:",
                    formDataPreview?.length
                  );
                  console.log("showFormPreview:", showFormPreview);
                  console.log("selectedForm:", selectedForm);
                  console.log("formKey:", formKey);
                  console.log("=== END DEBUG ===");

                  if (
                    Array.isArray(formDataPreview) &&
                    formDataPreview.length > 0
                  ) {
                    return (
                      <ReactFormGenerator
                        key={`preview-${formKey}-${selectedForm}-${formDataPreview.length}`}
                        data={formDataPreview}
                        action_name="Submit Preview"
                        onSubmit={handleAnswerSave}
                        answer_data={{}}
                        hide_actions={false}
                      />
                    );
                  } else {
                    return (
                      <Box>
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          align="center"
                        >
                          This form has no fields to display.
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          align="center"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          {formDataPreview === null
                            ? "Loading form data..."
                            : "Please edit the form to add elements or create a new form with fields."}
                        </Typography>
                      </Box>
                    );
                  }
                })()}
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* Modal for Available Forms */}
      <Modal
        open={openOtherForms}
        onClose={() => setOpenOtherForms(false)}
        aria-labelledby="other-forms-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography
            id="other-forms-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
          >
            Select Registration Form
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Choose which form to use for event registration. Click "Use This
            Form" to assign it to this event.
          </Typography>
          <List>
            {forms.map((form) => (
              <ListItem
                key={form._id}
                divider
                button
                onClick={() => handleFormSelect(form._id)}
                sx={{
                  borderLeft:
                    form._id === eventFormId
                      ? "4px solid #4caf50"
                      : form._id === selectedForm
                      ? "3px solid #1976d2"
                      : "none",
                  bgcolor:
                    form._id === eventFormId
                      ? "rgba(76, 175, 80, 0.08)"
                      : form._id === selectedForm
                      ? "rgba(25, 118, 210, 0.08)"
                      : "transparent",
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      component="span"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      {form.name}
                      {form._id === eventFormId && (
                        <Chip
                          size="small"
                          label="Event Form"
                          color="success"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    form.description || `${(form.formData || []).length} fields`
                  }
                />
                <ListItemSecondaryAction>
                  {form._id !== eventFormId ? (
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAssignFormToEvent(form._id);
                        setOpenOtherForms(false);
                      }}
                      color="primary"
                      variant="contained"
                    >
                      Use This Form
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      color="success"
                      variant="outlined"
                      disabled
                    >
                      Currently Used
                    </Button>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setOpenOtherForms(false)}>Close</Button>
          </Box>
        </Box>
      </Modal>

      {/* Form Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Form Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Make this the global default form for all new events?
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              sx={{ mb: 2 }}
            >
              Note: This is different from assigning it to the current event.
              Global default forms are automatically selected for new events.
            </Typography>
            <Box display="flex" alignItems="center" mb={3}>
              <Button
                variant={
                  selectedFormSettings.isDefault ? "contained" : "outlined"
                }
                color="primary"
                onClick={() =>
                  setSelectedFormSettings({
                    ...selectedFormSettings,
                    isDefault: true,
                  })
                }
                sx={{ mr: 2 }}
              >
                Yes
              </Button>
              <Button
                variant={
                  !selectedFormSettings.isDefault ? "contained" : "outlined"
                }
                color="primary"
                onClick={() =>
                  setSelectedFormSettings({
                    ...selectedFormSettings,
                    isDefault: false,
                  })
                }
              >
                No
              </Button>
            </Box>

            <TextField
              label="Form Description"
              fullWidth
              multiline
              rows={3}
              value={selectedFormSettings.description}
              onChange={(e) =>
                setSelectedFormSettings({
                  ...selectedFormSettings,
                  description: e.target.value,
                })
              }
              helperText="Briefly describe the purpose of this form"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveFormSettings}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for form builder */}
      <Dialog
        fullScreen
        open={openFormBuilder}
        onClose={() => setOpenFormBuilder(false)}
        PaperProps={{
          style: {
            height: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Form Builder</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setOpenFormBuilder(false)}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ display: "flex", flexDirection: "column", p: 0 }}>
          <Box sx={{ p: 2 }}>
            <TextField
              label="Form Name"
              value={formName}
              onChange={handleFormNameChange}
              placeholder="Enter form name"
              variant="outlined"
              fullWidth
              margin="normal"
              required
              error={!formName.trim() && formData.length > 0}
              helperText={
                !formName.trim() && formData.length > 0
                  ? "Form name is required"
                  : ""
              }
            />
          </Box>

          <Divider />

          <Box sx={{ display: "flex", height: "calc(100vh - 220px)" }}>
            <FormPreviewPanel>
              <Typography variant="h6" mb={2}>
                Form Preview
              </Typography>
              <Paper sx={{ p: 2, minHeight: "300px" }}>
                {Array.isArray(formData) && formData.length > 0 ? (
                  <ReactFormGenerator
                    key={`builder-${formKey}`}
                    data={formData}
                    hide_actions
                  />
                ) : (
                  <Typography
                    color="textSecondary"
                    align="center"
                    sx={{ py: 10 }}
                  >
                    Add form elements from the right panel to see the preview
                    here
                  </Typography>
                )}
              </Paper>
            </FormPreviewPanel>

            <FormBuilderPanel>
              <Typography variant="h6" mb={2}>
                Drag and Drop Elements
              </Typography>
              <ReactFormBuilder onPost={handleFormSave} />
            </FormBuilderPanel>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenFormBuilder(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveForm}
            color="primary"
            variant="contained"
            disabled={loading || !formName.trim() || !formData.length}
          >
            {loading ? <CircularProgress size={24} /> : "Save Form"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RegistrationForm;
